import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import { LoaderView } from "../loader/loader.comp";
import { parseAuthCallback } from "../../common/utilities/auth-domain/auth-url.util";
import { registerFirebase } from "../../api/auth/endpoints-referrers";
import { AxiosError } from "axios";
import { updateUser } from "../../api/users/endpoints-referrers";
import { AuthErrorMessage } from "../../common/enums/auth-error-message.enum";

/**
 * Turn the backend's answer to the first /users/current into something a
 * person can act on. Tenant auth policy refusals (tenant_authentication_plan.md
 * §2.2) come back as 403 { code, message } once the user is already signed
 * in at the IdP, so this page is the only place they can be shown; a 422 is
 * the historical "no account on this tenant" for an uninvited first login.
 */
/** Claims of the Firebase ID token this page was handed (unverified read; the backend verifies). */
const tokenClaims = (
    token: string
): { email?: string; email_verified?: boolean; sign_in_provider?: string } => {
    try {
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        return {
            email: payload?.email,
            email_verified: payload?.email_verified,
            sign_in_provider: payload?.firebase?.sign_in_provider
        };
    } catch {
        return {};
    }
};

const describeCallbackError = (err: any): string => {
    const status = err?.response?.status;
    const code = err?.response?.data?.code;
    if (status === 403) {
        switch (code) {
            case "domain_not_allowed":
                return AuthErrorMessage.domainNotAllowed;
            case "directory_not_allowed":
                return AuthErrorMessage.directoryNotAllowed;
            case "method_not_allowed":
                return AuthErrorMessage.methodNotAllowed;
            case "support_session_expired":
                return "The support session has expired";
            case "password_expired":
                return AuthErrorMessage.passwordExpired;
        }
    }
    if (status === 422) {
        return "There is no account for this email on this organization. Ask an administrator for an invitation.";
    }
    const raw = err?.response?.data?.message;
    const message = Array.isArray(raw) ? raw.join(" ") : raw;
    return (
        (typeof message === "string" && message) ||
        err?.message ||
        "Failed to complete authentication"
    );
};

/**
 * Handles the authentication callback from the auth domain
 * Extracts tokens from URL, stores them, loads user data, and redirects
 */
const AuthCallbackHandler = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setTokens, loadUser, setEmail, setEmailVerified } =
        FirebaseAuthContainer.useContainer();
    const [error, setError] = useState<string>("");
    const [isProcessing, setIsProcessing] = useState<boolean>(true);

    useEffect(() => {
        // Prevent multiple executions
        let executed = false;

        const processCallback = async () => {
            if (executed) return;
            executed = true;

            try {
                // Parse auth callback parameters
                const {
                    token,
                    refresh,
                    success,
                    error: authError
                } = parseAuthCallback(searchParams);

                // Check for authentication failure
                if (!success || authError) {
                    setError(authError || "Authentication failed");
                    setIsProcessing(false);
                    return;
                }

                // Validate tokens are present
                if (!token || !refresh) {
                    setError("Missing authentication tokens");
                    setIsProcessing(false);
                    return;
                }

                // Get OAuth-specific parameters
                const isOAuth = searchParams.get("isOAuth") === "true";
                const oauthEmail = searchParams.get("email");
                const invite = searchParams.get("invite");
                const firebaseUid = searchParams.get("firebaseUid");

                // Store tokens in localStorage first
                // This triggers loadUserData useEffect, but it will handle 422 gracefully
                setTokens({ accessToken: token, refreshToken: refresh });

                // For OAuth users with invite codes, register them
                // The registerFirebase call needs tokens to be set for authentication
                if (isOAuth && invite && oauthEmail && firebaseUid) {
                    try {
                        // D7: companyName/forceCreateKB left the register
                        // contract with self-service signup.
                        const signupData = {
                            email: oauthEmail,
                            from: invite,
                            firebaseId: firebaseUid
                        };
                        await registerFirebase(signupData);
                    } catch (error) {
                        // If user already exists (409), update their firebaseId
                        if (error instanceof AxiosError && error.response?.status === 409) {
                            await updateUser(firebaseUid);
                        } else {
                            console.error("[AuthCallback] OAuth registration failed:", error);
                            // Don't throw - try to load user anyway in case they already exist
                        }
                    }
                }

                // Load user data from backend
                // The backend returns emailVerified from Firebase token claims
                await loadUser();

                // Clear tokens from URL immediately (security)
                // Use replace: true to prevent tokens from staying in browser history
                navigate("/", { replace: true });
            } catch (err: any) {
                console.error(
                    "[AuthCallback] Sign-in could not be completed:",
                    err?.response?.status ?? "",
                    err?.response?.data?.code ?? err?.message
                );
                // Self-registered password user who has not clicked the
                // verification link yet (tenant_authentication_plan.md Phase 5,
                // D8): there is no account to load until the first VERIFIED
                // login, so a 422 here is "confirm your email", not "no account".
                // The login domain has already re-sent the verification mail.
                const claims = tokenClaims(searchParams.get("token") ?? "");
                if (
                    err?.response?.status === 422 &&
                    claims.sign_in_provider === "password" &&
                    claims.email_verified === false
                ) {
                    setEmail(claims.email);
                    setEmailVerified(false);
                    navigate("/email-confirm", { replace: true });
                    return;
                }
                setError(describeCallbackError(err));
                setIsProcessing(false);
            }
        };

        processCallback();
    }, [searchParams, setTokens, loadUser, navigate, setEmail, setEmailVerified]);

    // Show error if authentication callback failed
    if (error) {
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    padding: "20px",
                    textAlign: "center"
                }}
            >
                <div
                    style={{
                        marginBottom: "20px",
                        fontSize: "24px",
                        fontWeight: 600,
                        color: "#d32f2f"
                    }}
                >
                    Authentication Error
                </div>
                <div
                    style={{
                        marginBottom: "30px",
                        fontSize: "16px",
                        color: "#666",
                        maxWidth: "500px",
                        lineHeight: "1.6"
                    }}
                >
                    {error}
                </div>
                <div style={{ fontSize: "14px", color: "#999" }}>
                    Please try logging in again. If the problem persists, contact support.
                </div>
            </div>
        );
    }

    // Show loading indicator while processing
    return <LoaderView show={isProcessing} />;
};

export default AuthCallbackHandler;
